import React, { useState, useRef, useMemo, useEffect } from 'react'
import { isEmpty } from 'ramda'
import debounce from 'lodash/debounce'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useMutation } from '@tanstack/react-query'
import { makeStyles } from '@material-ui/core/styles'
import {
  MenuItem,
  Typography,
  InputAdornment,
  CircularProgress,
  Popover,
} from '@material-ui/core'

import effects from 'app/effects/effects'
import MessageAlert from 'app/components/MessageAlert'
import SimpleInput from 'app/componentV4/Input/SimpleInput'

const useStyles = makeStyles((theme) => ({
  suggestionItem: {
    padding: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))

export default function GooglePlace({
  name,
  value,
  error,
  onChange,
  onBlur,
  placeholder,
  helperText,
  ...rest
}) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const anchorRef = useRef(null)
  const [address, setAddress] = useState(value || '')
  const [suggestions, setSuggestions] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)

  const googleCurated = useMutation({
    mutationFn: async (value) => {
      const response = await effects.apis.onboarding.googleCurated({
        placeName: value,
      })
      return response.data
    },
    onError: (err) => {
      const msgName = err.response?.data?.error?.name
      if (!['PlaceNotFoundError', 'GoogleFailure'].includes(msgName)) {
        dispatch(
          MessageAlert.showError({
            message:
              msgName || formatMessage({ id: 'error.InternalServerError' }),
          }),
        )
      }
    },
  })

  // Debounced search
  const search = useMemo(
    () =>
      debounce((val) => {
        if (val.trim()) googleCurated.mutate(val)
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Mirror external value
  useEffect(() => {
    setAddress(value || '')
  }, [value])

  // Update suggestions & anchor
  useEffect(() => {
    setSuggestions(googleCurated.data ?? [])
    setAnchorEl(
      googleCurated?.data?.length && anchorRef.current
        ? anchorRef.current
        : null,
    )
  }, [googleCurated.data])

  const handleInputChange = (e) => {
    const val = e.target.value
    setAddress(val)
    onChange(val)
    search(val)
  }

  const handleSelect = (item) => {
    onChange(item)
    setAnchorEl(null)
  }

  const handleBlur = (e) => {
    search.cancel()
    googleCurated.reset()
    setTimeout(() => setAnchorEl(null), 0)
    if (onBlur) onBlur(e)
  }

  return (
    <>
      <SimpleInput
        name={name}
        placeholder={placeholder}
        value={address}
        onChange={handleInputChange}
        onBlur={handleBlur}
        inputRef={anchorRef}
        error={error}
        helperText={helperText}
        fullWidth
        endAdornment={
          <InputAdornment position="end">
            {googleCurated.isPending && <CircularProgress size={14} />}
          </InputAdornment>
        }
        {...rest}
      />

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && !isEmpty(suggestions)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          style: {
            width: anchorEl?.clientWidth,
            maxHeight: 200,
            overflowY: 'auto',
            border: `1px solid #ccc`,
            borderRadius: 12,
            boxShadow: 'none',
          },
        }}
      >
        {suggestions.map((opt, i) => (
          <MenuItem
            key={i}
            className={classes.suggestionItem}
            onMouseDown={() => handleSelect(opt)}
          >
            <div>
              <Typography className="text-primary text-14">
                {opt.displayName}
              </Typography>
              <Typography className="text-gray-400 text-12">
                {opt.address}
              </Typography>
            </div>
          </MenuItem>
        ))}
      </Popover>
    </>
  )
}

GooglePlace.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  error: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
}
